import { CloseButton, Combobox, InputBase, ScrollArea, Text, Tooltip, useCombobox } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconChevronDown } from '@tabler/icons-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import superAdminAccents from './SuperAdmin/superAdminAccents.module.css';

/**
 * A reusable searchable combobox component with custom selected option highlighting.
 *
 * @param {Object} props
 * @param {Array<{value: string, label: string, disabled?: boolean, disabledReason?: string, rightSection?: import('react').ReactNode}>} props.options - Array of options to display. `disabled` greys out and blocks selection of an option; `disabledReason` (if provided) is shown in a tooltip on hover; `rightSection` (if provided) renders right-aligned after the label (labels stay strings so search keeps working).
 * @param {string} props.value - Currently selected value
 * @param {function} props.onChange - Callback when an option is selected
 * @param {string} [props.placeholder] - Placeholder text when no option is selected
 * @param {string} [props.searchPlaceholder] - Placeholder text for the search input
 * @param {string} [props.emptyMessage] - Message shown when no options match search
 * @param {boolean} [props.disabled] - Whether the combobox is disabled
 * @param {boolean} [props.error] - Whether to show error state
 * @param {function} [props.onBlur] - Callback when the combobox loses focus
 */
const SearchableCombobox = ({
  options,
  value,
  onChange,
  placeholder = '(Select option)',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No options found',
  disabled = false,
  error = false,
  onBlur,
  clearable,
  style,
}) => {
  const [search, setSearch] = useState('');
  const selectedOptionRef = useRef(null);
  const viewportRef = useRef(null);

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch('');
    },
    onDropdownOpen: () => {
      combobox.focusSearchInput();
    },
  });

  // Scroll to selected option when dropdown opens
  useEffect(() => {
    if (combobox.dropdownOpened && selectedOptionRef.current && viewportRef.current) {
      // Small delay to ensure DOM is ready
      requestAnimationFrame(() => {
        selectedOptionRef.current?.scrollIntoView({ block: 'center' });
      });
    }
  }, [combobox.dropdownOpened]);

  const filteredOptions = useMemo(() => {
    const searchLower = search.toLowerCase().trim();
    if (!searchLower) return options;
    return options.filter((opt) => opt.label.toLowerCase().includes(searchLower));
  }, [options, search]);

  const selectedOption = useMemo(() => options.find((opt) => opt.value === value), [options, value]);

  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      onChange(null);
    },
    [onChange]
  );

  const showClearButton = clearable && value && !disabled;

  return (
    <Combobox
      store={combobox}
      position="bottom-start"
      withArrow
      onOptionSubmit={(selectedValue) => {
        onChange(selectedValue);
        combobox.closeDropdown();
      }}
      style={style}
    >
      <Combobox.Target>
        <InputBase
          component="button"
          type="button"
          pointer
          disabled={disabled}
          error={error}
          rightSection={
            showClearButton ? (
              <CloseButton size="sm" onClick={handleClear} aria-label="Clear selection" />
            ) : (
              <IconChevronDown size={14} />
            )
          }
          rightSectionPointerEvents={showClearButton ? 'all' : 'none'}
          onClick={() => combobox.toggleDropdown()}
          onBlur={onBlur}
        >
          <Text size="sm" truncate c={selectedOption ? '' : 'dimmed'}>
            {selectedOption?.label || placeholder}
          </Text>
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Search
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder={searchPlaceholder}
        />
        <Combobox.Options>
          <ScrollArea.Autosize type="auto" mah={300} viewportRef={viewportRef}>
            {combobox.dropdownOpened &&
              (filteredOptions.length > 0 ? (
                filteredOptions.map((opt) => {
                  const isSelected = opt.value === value;
                  const row = (
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        pointerEvents: opt.disabled ? 'auto' : undefined,
                      }}
                    >
                      {isSelected && <IconCheck size={14} />}
                      {opt.disabled && <IconAlertTriangle size={14} color="var(--mantine-color-orange-6)" />}
                      <span style={{ color: opt.disabled ? 'var(--mantine-color-dimmed)' : undefined }}>
                        {opt.label}
                      </span>
                      {opt.rightSection && <span style={{ marginLeft: 'auto' }}>{opt.rightSection}</span>}
                    </span>
                  );
                  return (
                    <Combobox.Option
                      key={opt.value}
                      value={opt.value}
                      disabled={opt.disabled}
                      className={superAdminAccents.option}
                      ref={isSelected ? selectedOptionRef : undefined}
                      style={isSelected ? { backgroundColor: 'var(--mantine-color-primary-light)' } : undefined}
                    >
                      {opt.disabled && opt.disabledReason ? (
                        <Tooltip
                          label={opt.disabledReason}
                          position="right"
                          withArrow
                          events={{ hover: true, focus: true, touch: true }}
                        >
                          {row}
                        </Tooltip>
                      ) : (
                        row
                      )}
                    </Combobox.Option>
                  );
                })
              ) : (
                <Combobox.Empty>{emptyMessage}</Combobox.Empty>
              ))}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default React.memo(SearchableCombobox);
